"use client";

import {useCallback, useEffect, useMemo, useRef} from "react";
import {DialogsEnum} from "@/lib/enums/DialogsEnum";
import {useAppSelector} from "@/lib/hooks";
import {selectTopDialog, selectVisibleDialogs} from "@/lib/slices/dialogsSlice";
import {getOrchestrator} from "../singleton";
import {
    DialogEventListener,
    DialogEventType,
    DialogResult,
    OpenOptions,
} from "../types";

/** The orchestrator singleton (stable reference). */
export function useOrchestrator() {
    return getOrchestrator();
}

/**
 * Imperative open helper.
 *
 *   const open = useOpenDialog();
 *   const {result} = open(DialogsEnum.WALLET);
 *   const res = await result; // resolves when WALLET finally closes
 */
export function useOpenDialog() {
    return useCallback(
        (id: DialogsEnum, opts?: OpenOptions) => getOrchestrator().open(id, opts),
        [],
    );
}

/** Open and await the close result in one call (for async flows). */
export function useRequestDialog() {
    return useCallback(
        (id: DialogsEnum, opts?: OpenOptions): Promise<DialogResult> =>
            getOrchestrator().open(id, opts).result,
        [],
    );
}

export function useCloseDialog() {
    return useCallback(
        (instanceId: string, result?: Partial<DialogResult>) =>
            getOrchestrator().close(instanceId, result),
        [],
    );
}

/** Whether any instance of `id` is currently visible (reads the Redux mirror). */
export function useIsDialogOpen(id: DialogsEnum): boolean {
    const visible = useAppSelector(selectVisibleDialogs);
    return useMemo(() => visible.some((v) => v.id === id), [visible, id]);
}

/** The top-most visible dialog, or undefined. */
export function useTopDialog() {
    return useAppSelector(selectTopDialog);
}

/** All visible dialogs (ordered by z-index). */
export function useVisibleDialogs() {
    return useAppSelector(selectVisibleDialogs);
}

/** Lock/unlock a flow with automatic cleanup on unmount. */
export function useFlowLock() {
    const lock = useCallback(
        (...args: Parameters<ReturnType<typeof getOrchestrator>["lockFlow"]>) =>
            getOrchestrator().lockFlow(...args),
        [],
    );
    const unlock = useCallback((name: string) => getOrchestrator().unlockFlow(name), []);
    return {lock, unlock};
}

/**
 * Subscribe to lifecycle events for side-effects (analytics, sounds, focus
 * traps…). Handler is kept fresh without re-subscribing.
 */
export function useDialogEvent(type: DialogEventType, handler: DialogEventListener): void {
    const handlerRef = useRef(handler);
    useEffect(() => {
        handlerRef.current = handler;
    });
    useEffect(() => {
        const off = getOrchestrator().on(type, (event) => handlerRef.current(event));
        return off;
    }, [type]);
}

/**
 * Props for an individual dialog component to wire into the orchestrator.
 * Returns `open` + an `onOpenChange` that closes the instance when dismissed.
 */
export function useDialogController(instanceId: string) {
    const close = useCloseDialog();
    return useMemo(
        () => ({
            open: true,
            onOpenChange: (next: boolean) => {
                if (!next) close(instanceId, {success: false, reason: "user"});
            },
            /** Close as a success (confirmed / completed). */
            resolve: (data?: unknown) => close(instanceId, {success: true, reason: "programmatic", data}),
            /** Close as a dismissal (cancelled / declined). */
            dismiss: (data?: unknown) => close(instanceId, {success: false, reason: "user", data}),
        }),
        [instanceId, close],
    );
}
